import type { CaptureResult } from 'posthog-js'

import { INSURANCE_VERTICAL_PATHS } from '@/config/insuranceVerticals'

/**
 * Tags every PostHog event this app sends with the product it belongs to and the area of the site
 * it came from, so each one stays attributable once all products report to one PostHog project.
 *
 * The product values are the slugs in cailabs/utils/products.py. This app serves the marketing site
 * for every product as well as the Insure and Bookkeeper apps, so events without a product prefix
 * (PostHog's own `$` events, `marketing_cta_clicked`) take the product from the page's path.
 *
 * Cai, the AI CFO, at /cai-agent is a staff-only tool and not a product in code yet, so its pages
 * count as platform, as products.for_path does on the server.
 */

type Product = 'fuelapp' | 'bookkeeper' | 'insure' | 'platform'
type SiteArea = 'marketing' | 'app' | 'internal'

const PREFIXED_PRODUCTS: Product[] = ['fuelapp', 'bookkeeper', 'insure']

// Each prefix covers the path and everything below it. Unlisted paths (/, /cfo, /terms…) are platform.
const PATH_PRODUCTS: [string, Product][] = [
  ['/fuelapp', 'fuelapp'],
  // The shell's Fuel tab, an offer for FuelApp.
  ['/fuel', 'fuelapp'],
  ['/bookkeeper', 'bookkeeper'],
  ['/accountant', 'bookkeeper'],
  ...[
    '/insurance',
    '/group-captive',
    '/application',
    '/applications',
    '/quotes',
    '/sign-up',
    '/sign-envelope',
    '/claims',
    '/login-token',
    '/new',
    '/insure',
    // Insure's app before it moved to /insure, which old links still open first.
    '/app',
    '/embed',
    '/embed-demo',
  ].map((prefix): [string, Product] => [prefix, 'insure']),
]

// The landing pages. Everything else is a product flow ('app') unless it is a staff tool.
const MARKETING_PAGES = [
  '/',
  '/insurance',
  '/group-captive',
  '/how-we-use-ai',
  '/privacy',
  '/terms',
  '/cfo',
  '/accountant',
]
const MARKETING_SECTIONS = [
  '/fuelapp',
  '/insurance/large-fleet-coverage',
  '/insurance/amazon-relay',
  ...INSURANCE_VERTICAL_PATHS,
]
const INTERNAL_SECTIONS = ['/insure/internal', '/app/internal', '/design-system', '/cai-agent']

/** Whether `pathname` is `prefix` or below it, so `/insure` does not claim `/insurance`. */
function isUnder(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`)
}

function withoutTrailingSlash(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
}

export function productFor(event: string, pathname: string): Product {
  const prefixed = PREFIXED_PRODUCTS.find((product) => event.startsWith(`${product}_`))
  if (prefixed) return prefixed
  const path = withoutTrailingSlash(pathname)
  return PATH_PRODUCTS.find(([prefix]) => isUnder(path, prefix))?.[1] ?? 'platform'
}

export function siteAreaFor(pathname: string, hostname: string): SiteArea {
  const path = withoutTrailingSlash(pathname)
  if (hostname.startsWith('console.') || INTERNAL_SECTIONS.some((prefix) => isUnder(path, prefix)))
    return 'internal'
  if (MARKETING_PAGES.includes(path) || MARKETING_SECTIONS.some((prefix) => isUnder(path, prefix)))
    return 'marketing'
  // Bookkeeper's landing page on the marketing site; anywhere else, Bookkeeper itself.
  if (path === '/bookkeeper' && hostname.startsWith('www.')) return 'marketing'
  return 'app'
}

/** The page an event describes: the URL PostHog recorded on it, else the current one. */
function eventLocation(event: CaptureResult): { pathname: string; hostname: string } {
  const recorded = event.properties.$current_url
  if (typeof recorded === 'string') {
    try {
      return new URL(recorded)
    } catch {
      // Not a URL PostHog wrote; fall back to where the browser is now.
    }
  }
  return window.location
}

/** posthog-js `before_send` hook. Leaves any `product` or `site_area` a capture set itself. */
export function tagProduct(event: CaptureResult | null): CaptureResult | null {
  if (!event) return event
  const { pathname, hostname } = eventLocation(event)
  event.properties.product ??= productFor(event.event, pathname)
  event.properties.site_area ??= siteAreaFor(pathname, hostname)
  return event
}
